import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Plus, Pencil, Trash2, Loader2, MapPin, Layers, ChevronDown, ChevronRight, Link, X } from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

export default function LocationBatchPage() {
  const [locations, setLocations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'loc-create' | 'loc-edit' | 'batch-create' | 'batch-edit' | 'batch-assign'
  const [selected, setSelected] = useState(null);
  const [locForm, setLocForm] = useState({ name: '', address: '' });
  const [batchForm, setBatchForm] = useState({ name: '', description: '' });
  const [assignBatchId, setAssignBatchId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedLoc, setExpandedLoc] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/locations`),
        axios.get(`${BACKEND_URL}/api/batches`),
      ]);
      setLocations(l.data);
      setBatches(b.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLocSave = async () => {
    if (!locForm.name) { setError('Location name required'); return; }
    setSaving(true); setError('');
    try {
      if (dialog === 'loc-create') {
        await axios.post(`${BACKEND_URL}/api/admin/locations`, locForm);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/locations/${selected.id}`, locForm);
      }
      load(); setDialog(null);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleBatchSave = async () => {
    if (!batchForm.name.trim()) { setError('Batch name is required'); return; }
    setSaving(true); setError('');
    try {
      if (dialog === 'batch-create') {
        await axios.post(`${BACKEND_URL}/api/admin/batches`, batchForm);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/batches/${selected.id}`, batchForm);
      }
      load(); setDialog(null);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleAssignBatch = async () => {
    if (!assignBatchId) { setError('Select a batch'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/locations/${selected.id}/batches`, { batch_id: assignBatchId });
      load(); setDialog(null);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleUnassignBatch = async (locId, batchId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/locations/${locId}/batches/${batchId}`);
      load();
    } catch (e) { console.error(e); }
  };

  const deleteLoc = async (id) => {
    if (!window.confirm('Delete location? This cannot be undone.')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/locations/${id}`);
    load();
  };

  const deleteBatch = async (id) => {
    if (!window.confirm('Delete batch? This will remove it from all locations.')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/batches/${id}`);
    load();
  };

  // Compute unassigned batches per location
  const getUnassigned = (loc) => {
    const assignedIds = new Set(loc.batch_ids || []);
    return batches.filter(b => !assignedIds.has(b.id));
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Locations & Batches</h1>
            <p className="text-sm text-muted-foreground">Manage academy locations and global training batches</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setLocForm({name:'',address:''}); setError(''); setDialog('loc-create'); }}>
              <Plus size={14} className="mr-1" /> Location
            </Button>
            <Button size="sm" className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold" onClick={() => { setBatchForm({name:'',description:''}); setError(''); setDialog('batch-create'); }}>
              <Plus size={14} className="mr-1" /> Batch
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-[#111827] rounded-xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* ── Batches Section ── */}
            <Card className="bg-[#111827] border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={16} className="text-[#F7941D]" />
                  <h2 className="font-semibold text-sm">Batches</h2>
                  <Badge variant="secondary" className="text-[10px]">{batches.length}</Badge>
                </div>
                {batches.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No batches created yet. Create a global batch to get started.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {batches.map(batch => (
                      <div key={batch.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary/40 border border-border/30">
                        <span className="text-sm">{batch.name}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 ml-0.5" onClick={() => { setSelected(batch); setBatchForm({name:batch.name,description:batch.description||''}); setError(''); setDialog('batch-edit'); }}>
                          <Pencil size={11} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive/60 hover:text-destructive" onClick={() => deleteBatch(batch.id)}>
                          <Trash2 size={11} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── Locations Section ── */}
            <div className="space-y-3">
              {locations.map(loc => {
                const assignedBatches = (loc.batch_ids || [])
                  .map(id => batches.find(b => b.id === id))
                  .filter(Boolean);
                const unassigned = getUnassigned(loc);
                const isExpanded = expandedLoc === loc.id;
                return (
                  <Card key={loc.id} className="bg-[#111827] border-border/60">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedLoc(isExpanded ? null : loc.id)}>
                          <div className="w-9 h-9 bg-[#2BB5D8]/15 rounded-lg flex items-center justify-center">
                            <MapPin size={16} className="text-[#2BB5D8]" />
                          </div>
                          <div>
                            <p className="font-semibold">{loc.name}</p>
                            <p className="text-xs text-muted-foreground">{loc.address || 'No address'} · {assignedBatches.length} batch{assignedBatches.length !== 1 ? 'es' : ''}</p>
                          </div>
                          {isExpanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelected(loc); setLocForm({name:loc.name,address:loc.address||''}); setError(''); setDialog('loc-edit'); }}><Pencil size={14} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive/70 hover:text-destructive" onClick={() => deleteLoc(loc.id)}><Trash2 size={14} /></Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pl-12 space-y-2">
                          {assignedBatches.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No batches assigned to this location</p>
                          ) : assignedBatches.map(batch => (
                            <div key={batch.id} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/30">
                              <div className="flex items-center gap-2">
                                <Layers size={14} className="text-[#F7941D]" />
                                <p className="text-sm font-medium">{batch.name}</p>
                              </div>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive" onClick={() => handleUnassignBatch(loc.id, batch.id)} title="Remove from location">
                                <X size={12} />
                              </Button>
                            </div>
                          ))}
                          {unassigned.length > 0 ? (
                            <Button size="sm" variant="ghost" className="text-xs text-primary" onClick={() => { setSelected(loc); setAssignBatchId(''); setError(''); setDialog('batch-assign'); }}>
                              <Link size={12} className="mr-1" /> Add Existing Batch
                            </Button>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">All available batches are assigned to this location.</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Location Dialog */}
        <Dialog open={dialog === 'loc-create' || dialog === 'loc-edit'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-md">
            <DialogHeader><DialogTitle>{dialog === 'loc-create' ? 'Add Location' : 'Edit Location'}</DialogTitle></DialogHeader>
            {error && <Alert variant="destructive" className="bg-destructive/10 border-destructive/30"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-4">
              <div><Label>Location Name</Label><Input value={locForm.name} onChange={e => setLocForm(p => ({...p, name: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" /></div>
              <div><Label>Address</Label><Input value={locForm.address} onChange={e => setLocForm(p => ({...p, address: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" /></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>Cancel</Button>
                <Button className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold" onClick={handleLocSave} disabled={saving}>
                  {saving && <Loader2 size={14} className="animate-spin mr-1" />} Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Batch Dialog (global — no location) */}
        <Dialog open={dialog === 'batch-create' || dialog === 'batch-edit'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-md">
            <DialogHeader><DialogTitle>{dialog === 'batch-create' ? 'Create Batch' : 'Edit Batch'}</DialogTitle></DialogHeader>
            {error && <Alert variant="destructive" className="bg-destructive/10 border-destructive/30"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-4">
              <div><Label>Batch Name</Label><Input value={batchForm.name} onChange={e => setBatchForm(p => ({...p, name: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" placeholder="e.g. Beginner" /></div>
              <div><Label>Description (optional)</Label><Input value={batchForm.description} onChange={e => setBatchForm(p => ({...p, description: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" /></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>Cancel</Button>
                <Button className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold" onClick={handleBatchSave} disabled={saving}>
                  {saving && <Loader2 size={14} className="animate-spin mr-1" />} Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Batch Dialog */}
        <Dialog open={dialog === 'batch-assign'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-md">
            <DialogHeader><DialogTitle>Add Existing Batch — {selected?.name}</DialogTitle></DialogHeader>
            {error && <Alert variant="destructive" className="bg-destructive/10 border-destructive/30"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-4">
              <div>
                <Label>Select Batch</Label>
                <Select value={assignBatchId} onValueChange={setAssignBatchId}>
                  <SelectTrigger className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Choose a global batch" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {getUnassigned(selected || {}).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>Cancel</Button>
                <Button className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold" onClick={handleAssignBatch} disabled={saving}>
                  {saving && <Loader2 size={14} className="animate-spin mr-1" />} Add
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
